import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BATCH_NAME_MAX_LENGTH, hasDisallowedBatchNameChars } from '../sanitizeBatchName'

import { ConfirmationResultModal, FormField, TextInput } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { RenameDisbursementBatchModalProps } from './RenameDisbursementBatchModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function RenameDisbursementBatchModal({
  isOpen,
  onClose,
  disbursementBatchId,
  name,
  onSuccess
}: Readonly<RenameDisbursementBatchModalProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')

  // Batches predating names start empty.
  const initialName = name ?? ''
  const [batchName, setBatchName] = useState(initialName)

  const [{ isPending, errorMessage }, { mutateAsync: updateDisbursementBatch }] = useMutation(
    disbursementsService.updateDisbursementBatch
  )

  // Trimmed both sides, so a stored name with stray whitespace doesn't read as an edit.
  const hasChanges = useMemo(() => batchName.trim() !== initialName.trim(), [batchName, initialName])

  // Flagged, not stripped: rewriting the field would mangle the part never touched.
  const isInvalid = useMemo(() => hasDisallowedBatchNameChars(batchName), [batchName])

  const resetForm = () => {
    setBatchName(initialName)
    setModalState('confirm')
  }

  const handleClose = () => {
    if (!isPending) {
      resetForm()
      onClose()
    }
  }

  const handleSuccessClose = () => {
    resetForm()
    onSuccess()
    onClose()
  }

  // The error screen offers a single "Close", so it closes and discards, per AC3.
  const handleFailureClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    try {
      await updateDisbursementBatch({ id: disbursementBatchId, name: batchName.trim() })
      setModalState('success')
    } catch {
      setModalState('failure')
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.renameDisbursementBatch.title')}
      primaryButtonText={t('button.saveChanges')}
      loadingText={t('modal.renameDisbursementBatch.button.saving')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleSubmit}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      disabled={!hasChanges || isInvalid || isPending}
      loading={isPending}
      primaryBtnTestId="rename-disbursement-batch-submit-button"
      secondaryBtnTestId="rename-disbursement-batch-cancel-button"
      success={{
        title: t('modal.renameDisbursementBatch.success.title'),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.renameDisbursementBatch.error.title'),
        // The API's reason, so a rejected name says why rather than just failing.
        description: errorMessage || undefined,
        button: t('button.close')
      }}
    >
      <FormField
        label={t('typography.name')}
        necessity="optional"
        status={isInvalid ? 'danger' : 'default'}
        statusMessage={isInvalid ? t('modal.renameDisbursementBatch.name.invalid') : ''}
      >
        <TextInput
          name="name"
          value={batchName}
          maxLength={BATCH_NAME_MAX_LENGTH}
          onChange={event => setBatchName(event.target.value)}
          data-testid="rename-disbursement-batch-name-input"
        />
      </FormField>
    </ConfirmationResultModal>
  )
}
